import React, { useState } from 'react';
import styled from 'styled-components';
import Panel from './Panel';
import PeriodSelector from './PeriodSelector';
import useMonitoringData from '../hooks/useMonitoring';
import {
  fetchSubscribeStats,
  fetchRevenueStats,
  fetchExtensionStats,
  fetchStandardStats,
} from '../api/monitoringApi';

const Table = styled.table`width: 100%; border-collapse: collapse; font-size: 13px;`;
const Th = styled.th`text-align: left; padding: 6px 8px; background: #f9fafb; border-bottom: 1px solid #e5e7eb; color: #6b7280; font-weight: 600;`;
const Td = styled.td`padding: 6px 8px; border-bottom: 1px solid #f3f4f6; color: #374151;`;
const DistRow = styled.div`display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 8px;`;
const DistItem = styled.div`
  font-size: 12px; padding: 4px 10px; border-radius: 6px; background: #f3f4f6; color: #374151;
  span { font-weight: 700; }
`;
const StatRow = styled.div`display: flex; gap: 24px; margin-bottom: 12px; flex-wrap: wrap;`;
const StatItem = styled.div`font-size: 13px; color: #6b7280; span { font-weight: 700; color: #111827; }`;

// ── 구독 현황 ──
export function SubscribeStatsPanel({ refreshKey }) {
  const { data, isLoading, error } = useMonitoringData(fetchSubscribeStats, [refreshKey]);
  return (
    <Panel title="구독 현황" isLoading={isLoading} error={error}>
      {data && <>
        <StatRow>
          <StatItem>총 구독: <span>{data.total}</span></StatItem>
          <StatItem>만료 예정 (7일): <span style={{color:'#f59e0b'}}>{data.expiring_soon}</span></StatItem>
          <StatItem>다운그레이드 예약: <span>{data.recent_downgrades}</span></StatItem>
        </StatRow>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>플랜별 분포</div>
        <DistRow>
          {Object.entries(data.by_plan || {}).map(([k, v]) => <DistItem key={k}>{k}: <span>{v}</span></DistItem>)}
        </DistRow>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, marginTop: 8 }}>상태별 분포</div>
        <DistRow>
          {Object.entries(data.by_status || {}).map(([k, v]) => <DistItem key={k}>{k}: <span>{v}</span></DistItem>)}
        </DistRow>
      </>}
    </Panel>
  );
}

// ── 매출 통계 ──
export function RevenuePanel({ refreshKey }) {
  const [period, setPeriod] = useState('30d');
  const { data, isLoading, error } = useMonitoringData(() => fetchRevenueStats(period), [period, refreshKey]);
  return (
    <Panel title="매출 통계" isLoading={isLoading} error={error}
      actions={<PeriodSelector value={period} onChange={setPeriod} options={['7d','30d','90d']} />}>
      {data && <>
        <StatRow>
          <StatItem>총 매출: <span>₩{data.total_revenue?.toLocaleString()}</span></StatItem>
          <StatItem>결제 건수: <span>{data.total_transactions}</span></StatItem>
        </StatRow>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>플랜별 매출</div>
        <DistRow>
          {Object.entries(data.by_plan || {}).map(([k, v]) => <DistItem key={k}>{k}: <span>₩{v?.toLocaleString()}</span></DistItem>)}
        </DistRow>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, marginTop: 8 }}>일별 매출</div>
        <div style={{ overflowX: 'auto' }}>
          <Table>
            <thead><tr><Th>날짜</Th><Th>금액</Th><Th>건수</Th></tr></thead>
            <tbody>
              {data.daily_revenue?.slice(-10).map((r, i) => (
                <tr key={i}><Td>{r.date}</Td><Td>₩{r.amount?.toLocaleString()}</Td><Td>{r.count}</Td></tr>
              ))}
            </tbody>
          </Table>
        </div>
      </>}
    </Panel>
  );
}

// ── 익스텐션 통계 ──
export function ExtensionStatsPanel({ refreshKey }) {
  const { data, isLoading, error } = useMonitoringData(fetchExtensionStats, [refreshKey]);
  return (
    <Panel title="익스텐션 통계" isLoading={isLoading} error={error}>
      {data && <>
        <StatRow>
          <StatItem>총 익스텐션: <span>{data.total}</span></StatItem>
          <StatItem>설치 수: <span>{data.total_configs}</span></StatItem>
        </StatRow>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>타입별</div>
        <DistRow>
          {Object.entries(data.by_type || {}).map(([k, v]) => <DistItem key={k}>{k}: <span>{v}</span></DistItem>)}
        </DistRow>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, marginTop: 8 }}>인기 Top 10</div>
        <Table>
          <thead><tr><Th>익스텐션</Th><Th>타입</Th><Th>설치 수</Th></tr></thead>
          <tbody>
            {data.top_installed?.map((r, i) => (
              <tr key={i}><Td>{r.title}</Td><Td>{r.extension_type}</Td><Td>{r.install_count}</Td></tr>
            ))}
          </tbody>
        </Table>
      </>}
    </Panel>
  );
}

// ── 공통: 7일 스파크라인 ──
const SparkWrap = styled.div`
  display: flex; align-items: flex-end; gap: 3px; height: 40px; margin-top: 6px;
`;
const SparkBar = styled.div`
  flex: 1; min-width: 6px; background: ${p => p.$color || '#93c5fd'}; border-radius: 2px 2px 0 0;
  height: ${p => Math.max(p.$pct, 4)}%;
  position: relative;
  &:hover::after {
    content: "${p => p.$label}";
    position: absolute; bottom: 100%; left: 50%; transform: translateX(-50%);
    background: #111827; color: #fff; font-size: 11px; padding: 2px 6px; border-radius: 4px;
    white-space: nowrap; margin-bottom: 4px;
  }
`;

function Sparkline({ points = [], color, emptyLabel = '데이터 없음' }) {
  if (!points.length) {
    return <div style={{ fontSize: 12, color: '#9ca3af', padding: '10px 0' }}>{emptyLabel}</div>;
  }
  const max = Math.max(...points.map(p => p.count), 1);
  return (
    <SparkWrap>
      {points.map((p, i) => (
        <SparkBar key={i} $pct={(p.count / max) * 100} $color={color} $label={`${p.date} · ${p.count}건`} />
      ))}
    </SparkWrap>
  );
}

const SectionGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 24px;
  @media (max-width: 1000px) { grid-template-columns: 1fr; }
`;
const SubLabel = styled.div`font-size: 12px; font-weight: 600; color: #6b7280; margin: 10px 0 4px;`;
const HighlightCard = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 12px; border-radius: 8px; margin-bottom: 8px;
  background: ${p => p.$bg || '#f9fafb'}; color: #111827;
  .label { font-size: 12px; color: #6b7280; }
  .val { font-size: 18px; font-weight: 700; color: ${p => p.$accent || '#111827'}; }
`;

// ── 스탠다드 플랜 운영 지표 ──
export function StandardPlanPanel({ refreshKey }) {
  const { data, isLoading, error } = useMonitoringData(fetchStandardStats, [refreshKey]);
  const sub = data?.subscription;
  const ext = data?.member_extension;
  const mem = data?.members;

  const extActiveRate = ext && ext.total_configs ? ((ext.active_configs / ext.total_configs) * 100).toFixed(1) : null;
  const extPerStandard = sub?.active ? (ext?.installed_sites / sub.active).toFixed(2) : null;

  return (
    <Panel title="스탠다드 플랜 운영 지표" isLoading={isLoading} error={error}>
      {data && <>
        {/* 상단 강조 카드 3개 */}
        <SectionGrid style={{gridTemplateColumns: 'repeat(3, 1fr)', gap: 12}}>
          <HighlightCard $bg="#ecfdf5" $accent="#047857">
            <div>
              <div className="label">스탠다드 활성 구독</div>
              <div className="val">{sub?.active ?? 0}<span style={{fontSize:12, color:'#6b7280', marginLeft:8}}>/ 누적 {sub?.total ?? 0}</span></div>
              <div style={{fontSize:11, color:'#6b7280', marginTop:4}}>최근 30일 신규 {sub?.new_30d ?? 0}{sub?.pending_downgrade > 0 && ` · 다운그레이드 예약 ${sub.pending_downgrade}`}</div>
            </div>
          </HighlightCard>
          <HighlightCard $bg="#eff6ff" $accent="#1d4ed8">
            <div>
              <div className="label">회원관리 익스텐션 설치 사이트</div>
              <div className="val">{ext?.installed_sites ?? 0}{extPerStandard && <span style={{fontSize:12, color:'#6b7280', marginLeft:8}}>구독당 {extPerStandard}</span>}</div>
              <div style={{fontSize:11, color:'#6b7280', marginTop:4}}>활성 설정 {ext?.active_configs ?? 0} / 전체 {ext?.total_configs ?? 0}{extActiveRate && ` (${extActiveRate}%)`}</div>
            </div>
          </HighlightCard>
          <HighlightCard $bg="#f5f3ff" $accent="#6d28d9">
            <div>
              <div className="label">총 사이트 회원</div>
              <div className="val">{mem?.total ?? 0}<span style={{fontSize:12, color:'#6b7280', marginLeft:8}}>그룹 {mem?.total_groups ?? 0}</span></div>
              <div style={{fontSize:11, color:'#6b7280', marginTop:4}}>7일 신규 {mem?.new_7d ?? 0} · 30일 활성 {mem?.active_30d ?? 0}</div>
            </div>
          </HighlightCard>
        </SectionGrid>

        {/* 중단 세부 지표 */}
        <SectionGrid style={{marginTop: 16}}>
          <div>
            <SubLabel>회원 분포</SubLabel>
            <StatRow>
              <StatItem>사이트당 평균: <span>{mem?.avg_per_site ?? 0}</span></StatItem>
              <StatItem>멤버 보유 사이트: <span>{mem?.sites_with_members ?? 0}</span></StatItem>
              <StatItem>그룹 운영 사이트: <span>{mem?.sites_with_groups ?? 0}</span></StatItem>
            </StatRow>
            <SubLabel>회원 유형</SubLabel>
            <DistRow>
              {Object.entries(mem?.by_type || {}).map(([k, v]) => <DistItem key={k}>{k}: <span>{v}</span></DistItem>)}
            </DistRow>
            <SubLabel>회원 상태</SubLabel>
            <DistRow>
              {Object.entries(mem?.by_status || {}).map(([k, v]) => <DistItem key={k}>{k}: <span>{v}</span></DistItem>)}
            </DistRow>
          </div>
          <div>
            <SubLabel>최근 7일 사이트 회원 가입 추이</SubLabel>
            <Sparkline points={data.recent_signups} color="#8b5cf6" />
          </div>
        </SectionGrid>

        {/* 하단 Top 10 */}
        {data.top_sites?.length > 0 && (
          <>
            <SubLabel style={{marginTop: 20}}>회원 보유 Top 10 사이트</SubLabel>
            <div style={{overflowX: 'auto'}}>
              <Table>
                <thead><tr><Th>사이트</Th><Th>도메인</Th><Th>소유자</Th><Th>회원 수</Th><Th>그룹 수</Th></tr></thead>
                <tbody>
                  {data.top_sites.map((r, i) => (
                    <tr key={i}>
                      <Td>{r.site_name || '-'}</Td>
                      <Td style={{color:'#6b7280'}}>{r.domain || '-'}</Td>
                      <Td>{r.client_name || r.client_email || '-'}</Td>
                      <Td style={{fontWeight:600}}>{r.member_count}</Td>
                      <Td>{r.group_count}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </>}
    </Panel>
  );
}
